/**
 * useListingHover Hook
 *
 * Custom hook for tracking listing hover events with throttling and debouncing
 * to prevent event spam while maintaining data quality.
 *
 * Performance Strategy:
 * - 500ms debounce: User must hover for 500ms before event fires (prevents accidental hovers)
 * - 3000ms throttle: Same listing tracked at most once per 3 seconds (prevents spam)
 * - Cleanup on mouse leave: Cancels pending events if user leaves quickly
 *
 * Estimated Impact:
 * - Reduces event volume by ~70% while maintaining data quality
 * - Without throttling: ~20K hover events/day
 * - With throttling: ~8K hover events/day
 */

import { useRef, useCallback } from 'react';
import { getAnalytics } from './analytics';
import { recordInterest } from './session-interest';
import type { ListingPageType } from './listing-page-type';

interface UseListingHoverParams {
  listingId: number;
  listingTitle?: string;
  listingPrice?: number;
  categoryId?: number;
  categoryGroupId?: number;
  resultPosition?: number;
  totalResults?: number;
  searchQuery?: string;
  pageType?: ListingPageType;
  activeFilters?: Record<string, string>;
  tags?: string[];
  /** Which rendered row the card sits in, so a row can be judged on its own. */
  listName?: string;
  /** 1-indexed position of that row on the page. */
  rowIndex?: number;
  throttleMs?: number; // Default: 3000ms (track at most once per 3 seconds per listing)
  debounceMs?: number; // Default: 500ms (user must hover for 500ms)
}

export function useListingHover({
  listingId,
  listingTitle,
  listingPrice,
  categoryId,
  categoryGroupId,
  resultPosition,
  totalResults,
  searchQuery,
  pageType,
  activeFilters,
  tags,
  listName,
  rowIndex,
  throttleMs = 3000,
  debounceMs = 500,
}: UseListingHoverParams) {
  // Track last hover time for throttling
  const lastHoverTime = useRef<number>(0);

  // Track debounce timeout
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Handle mouse enter event
   * Implements debounce (must hover for debounceMs) and throttle (max once per throttleMs)
   */
  const handleHover = useCallback(() => {
    const now = Date.now();

    // Throttle: Only track if enough time has passed since last hover
    if (now - lastHoverTime.current < throttleMs) {
      return;
    }

    // Clear any pending hover events
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Debounce: Wait for debounceMs to ensure it's a meaningful hover
    // (not just mouse passing through)
    hoverTimeoutRef.current = setTimeout(() => {
      lastHoverTime.current = now;

      // A dwell is weak evidence but it is evidence, and it arrives before any
      // click does -- which matters when the median session sees 3-5 tags.
      // Recorded here so every surface that tracks hover contributes.
      recordInterest({ signal: 'hover', tags, categoryGroupId });

      const analytics = getAnalytics();
      if (analytics) {
        analytics.listingHover({
          id: listingId,
          title: listingTitle,
          price: listingPrice,
          categoryId,
          categoryGroupId,
          resultPosition,
          totalResults,
          searchQuery,
          pageType,
          activeFilters,
          tags,
          listName,
          rowIndex,
        });
      }
    }, debounceMs);

  }, [
    listingId,
    listingTitle,
    listingPrice,
    categoryId,
    categoryGroupId,
    resultPosition,
    totalResults,
    searchQuery,
    pageType,
    activeFilters,
    tags,
    listName,
    rowIndex,
    throttleMs,
    debounceMs,
  ]);

  /**
   * Handle mouse leave event
   * Cancels pending hover event if user leaves quickly
   */
  const handleMouseLeave = useCallback(() => {
    // Cancel pending hover event if user leaves before debounce completes
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  return {
    onMouseEnter: handleHover,
    onMouseLeave: handleMouseLeave,
  };
}
